import { buildStatement } from "@/lib/customerStatement";

// Print a printable HTML document for a record (Quotation, Invoice, PO, etc.)
// Uses window.print() with a custom popup window containing nicely formatted HTML.

const fmt = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  if (typeof n === "number") return new Intl.NumberFormat("en-TZ").format(n);
  return String(n);
};

const KEY_LABELS = {
  invoice_no: "Invoice No.",
  quote_no: "Quote No.",
  order_no: "Order No.",
  po_no: "PO No.",
  pr_no: "PR No.",
  grn_no: "GRN No.",
  voucher_no: "Voucher No.",
  contract_no: "Contract No.",
  wo_no: "Work Order No.",
  ticket_no: "Ticket No.",
  emp_no: "Employee No.",
  asset_no: "Asset No.",
  customer: "Customer",
  supplier: "Supplier",
  amount: "Amount",
  tax: "Tax",
  total: "Total",
  status: "Status",
  due_date: "Due Date",
  date: "Date",
  scheduled_date: "Scheduled Date",
  valid_until: "Valid Until",
  description: "Description",
  purpose: "Purpose",
  requester: "Requester",
  technician: "Technician",
  priority: "Priority",
  category: "Category",
};

const label = (k) => KEY_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const escHtml = (v) => (v === null || v === undefined ? "" : String(v))
  .replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;" }[c]));

// The browser uses the print window's <title> as the default "Save as PDF"
// filename, so this IS the output file name: "<Invoice Title> <Invoice No.>",
// e.g. "CCTV Installation - Saifee Hospital (Amani) 5383-C". "/" in the
// number (5383/C) is swapped for "-" since it isn't valid in a filename and
// browsers would otherwise mangle it unpredictably. Falls back to the
// document type when an invoice has no title.
export function invoicePdfName(record) {
  const title = (record.invoice_title || "").trim() || record.doc_type || "Invoice";
  const number = (record.invoice_no || "").replace(/\//g, "-");
  return [title, number].filter(Boolean).join(" ").replace(/[\\:*?"<>|]/g, "");
}

// Renders an invoice as a PDF matching KaribuNet's real tax invoice template
// exactly (logo, TIN/ZRA, BILL TO + installation site, issue/expiry dates,
// line items, subtotal/VAT/total, dual bank payment details).
export function printInvoicePdf(record) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;

  const items = Array.isArray(record.items) && record.items.length
    ? record.items
    : [{ description: "Services rendered", quantity: 1, unit_price: record.amount || 0, total: record.amount || 0 }];
  const subtotal = items.reduce((s, it) => s + (Number(it.total) || (Number(it.quantity) || 0) * (Number(it.unit_price) || 0)), 0);
  const vatEnabled = !!record.vat_enabled;
  const tax = vatEnabled ? subtotal * 0.15 : (Number(record.tax) || 0);
  const total = subtotal + tax;
  const money = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
  // USD invoice: items and totals are in USD (see backend/routes/invoice_currency.py);
  // the TZS equivalent at the invoice's fixed rate is printed under the total.
  const isUsd = record.currency === "USD" && Number(record.exchange_rate) > 0;
  const cur = isUsd ? "USD" : "TZS";
  const rate = Number(record.exchange_rate) || 0;
  const dateFmt = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString("en-GB").replace(/\//g, "/");
  };

  const rows = items.map((it, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${it.description || it.product || ""}</td>
      <td class="c">${it.quantity ?? ""}</td>
      <td class="r">${money(it.unit_price)}</td>
      <td class="r">${money(it.total ?? (Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
    </tr>`).join("");

  const html = `<!doctype html>
<html><head><title>${escHtml(invoicePdfName(record))}</title>
<style>
  @page { size: A4; margin: 16mm; }
  body { font-family: 'Times New Roman', Georgia, serif; color: #111; margin: 0; padding: 24px; font-size: 13px; }
  h1 { font-size: 20px; margin: 0 0 18px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; }
  .company p { margin: 2px 0; font-weight: bold; }
  .company a { color: #111; }
  .logo { text-align: right; }
  .logo img { max-width: 190px; }
  .bill-to { margin-top: 28px; }
  .bill-to .label { font-weight: bold; }
  .bill-to .name { text-align: right; font-weight: bold; }
  .bill-to .site { margin-top: 4px; }
  .meta { display: flex; border: 1px solid #333; margin-top: 14px; }
  .meta div { flex: 1; padding: 8px 12px; border-right: 1px solid #333; }
  .meta div:last-child { border-right: none; }
  .meta b { display: block; font-size: 11px; }
  table.items { width: 100%; border-collapse: collapse; margin-top: 16px; }
  table.items th { background: #1e3a8a; color: #fff; padding: 8px; text-align: left; font-size: 12px; }
  table.items td { padding: 7px 8px; border-bottom: 1px solid #ddd; font-size: 12px; }
  table.items td.c, table.items th.c { text-align: center; }
  table.items td.r, table.items th.r { text-align: right; }
  tfoot td { padding: 8px; font-weight: bold; }
  tfoot tr.total-row td { background: #1e3a8a; color: #fff; }
  /* Browsers repeat a <tfoot> at the bottom of every printed page; render it
     as a normal row group so the totals appear once, after the last item,
     and keep the three total rows together on one page. */
  table.items tfoot { display: table-row-group; break-inside: avoid; page-break-inside: avoid; }
  table.items tfoot tr { break-inside: avoid; page-break-inside: avoid; }
  .payments { display: flex; justify-content: space-between; gap: 24px; margin-top: 24px; font-size: 12px; }
  .payments ul { margin: 4px 0; padding-left: 18px; }
  .note { font-style: italic; margin-top: 18px; font-size: 12px; text-align: center; }
  .footer { text-align: center; margin-top: 24px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 11px; }
  @media print { .noprint { display: none !important; } }
  .actions { position: fixed; top: 10px; right: 10px; }
  .actions button { padding: 8px 14px; border: 0; background: #1e3a8a; color: white; border-radius: 6px; cursor: pointer; font-weight: 600; }
</style></head>
<body>
  <div class="actions noprint"><button onclick="window.print()">Print / Save PDF</button></div>
  <h1>${(record.doc_type || "Tax Invoice").toUpperCase()}</h1>
  <div class="top">
    <div class="company">
      <p>KARIBUNET LIMITED</p>
      <p>P. O. Box 221</p>
      <p>ZANZIBAR, TANZANIA</p>
      <p><a href="https://www.karibunet.co.tz">www.karibunet.co.tz</a></p>
      <p><a href="mailto:info@karibunet.co.tz">info@karibunet.co.tz</a></p>
      <p style="margin-top:10px;">TIN NO: 175-484-191</p>
      <p>ZRA: Z052403695-030</p>
    </div>
    <div class="logo"><img src="/img/karibunet-logo.jpg" alt="KaribuNet" /></div>
  </div>

  <div class="bill-to">
    <span class="label">BILL TO:</span>
    <div class="name">${(record.customer || "").toString().toUpperCase()}</div>
    <div style="text-align:right;">Zanzibar, Tanzania</div>
    ${record.installation_site ? `<div class="site"><b>INSTALLATION SITE</b> &nbsp; ${record.installation_site}</div>` : ""}
  </div>

  <div class="meta">
    <div><b>Invoice Number:</b>${record.invoice_no || "—"}</div>
    <div><b>Issue Date:</b>${dateFmt(record.issue_date || record.created_at)}</div>
    <div><b>Expiry Date:</b>${dateFmt(record.due_date)}</div>
  </div>

  <table class="items">
    <thead>
      <tr><th class="c">Item #</th><th>Description</th><th class="c">Quantity</th><th class="r">Unit Price (${cur})</th><th class="r">Total (${cur})</th></tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr><td colspan="4" class="r">SUBTOTAL</td><td class="r">${money(subtotal)}</td></tr>
      <tr><td colspan="4" class="r">TAX ${vatEnabled ? "(15%)" : ""}</td><td class="r">${money(tax)}</td></tr>
      <tr class="total-row"><td colspan="4" class="r">TOTAL (${cur})</td><td class="r">${money(total)}</td></tr>
      ${isUsd ? `
      <tr><td colspan="4" class="r">EXCHANGE RATE (1 USD = TZS)</td><td class="r">${money(rate)}</td></tr>
      <tr class="total-row"><td colspan="4" class="r">TOTAL (TZS)</td><td class="r">${money(total * rate)}</td></tr>` : ""}
    </tfoot>
  </table>

  <div class="payments">
    <ul>
      <li><b>Bank Name:</b> National Microfinance Bank PLC (NMB)</li>
      <li><b>Account Name:</b> KaribuNet Limited</li>
      <li><b>Account Number:</b> 23410051618</li>
      <li><b>SWIFT Code:</b> NMIBTZTZ</li>
    </ul>
    <ul>
      <li><b>Bank Name:</b> EQUITY BANK</li>
      <li><b>Account Number:</b> 3014211915622</li>
      <li><b>Account Name:</b> KaribuNet Limited</li>
      <li><b>SWIFT Code:</b> EQBLTZTZ</li>
    </ul>
  </div>
  <p class="note">Timely payments are essential to ensure smooth project execution and final handover.</p>
  <div class="footer">
    info@karibunet.co.tz &nbsp;||&nbsp; +255 786 093 757 &nbsp;||&nbsp; +255 625 968 941 &nbsp;||&nbsp; www.karibunet.co.tz
    <p style="margin-top: 6px;">This invoice is system generated by KARIS.</p>
  </div>
  <script>setTimeout(() => window.print(), 400);</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
}

// A signed-off record for the customer once a work order is done: what was
// asked for, what was actually done, and -- the part that matters most for
// traceability -- the serial number and MAC address of every device
// installed on that job, plus a customer acknowledgement line. This is the
// document a technician hands (or emails) the customer at handover, and the
// one place all three (job, devices, sign-off) appear together on paper.
export function printWorkOrderCompletion(record) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;

  const devices = (Array.isArray(record.devices) ? record.devices : []).filter((d) => (d.serial_no || "").trim());
  const dateFmt = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString("en-GB");
  };
  const esc = (v) => (v === null || v === undefined ? "" : String(v)).replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));

  const deviceRows = devices.map((d, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${esc(d.device_name) || "—"}</td>
      <td>${esc(d.serial_no)}</td>
      <td>${esc(d.mac_address) || "—"}</td>
      <td>${esc(d.notes) || "—"}</td>
    </tr>`).join("");

  const html = `<!doctype html>
<html><head><title>Job Completion Certificate ${record.wo_no || ""}</title>
<style>
  @page { size: A4; margin: 16mm; }
  body { font-family: 'Times New Roman', Georgia, serif; color: #111; margin: 0; padding: 24px; font-size: 13px; }
  h1 { font-size: 20px; margin: 0 0 18px; letter-spacing: 0.5px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; }
  .company p { margin: 2px 0; font-weight: bold; }
  .logo img { max-width: 170px; }
  .meta { display: flex; flex-wrap: wrap; border: 1px solid #333; margin-top: 18px; }
  .meta div { flex: 1; min-width: 140px; padding: 8px 12px; border-right: 1px solid #333; border-bottom: 1px solid #333; }
  .meta div:last-child { border-right: none; }
  .meta b { display: block; font-size: 11px; }
  .section { margin-top: 20px; }
  .section h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 1px solid #999; padding-bottom: 4px; margin-bottom: 8px; }
  .section p { margin: 0; white-space: pre-wrap; }
  .empty { color: #888; font-style: italic; }
  table.devices { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.devices th { background: #1e3a8a; color: #fff; padding: 7px 8px; text-align: left; font-size: 12px; }
  table.devices td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 12px; }
  table.devices td.c, table.devices th.c { text-align: center; width: 32px; }
  .sign { display: flex; justify-content: space-between; gap: 40px; margin-top: 48px; }
  .sign .box { flex: 1; text-align: center; }
  .sign .line { border-top: 1px solid #333; margin-top: 40px; padding-top: 6px; font-size: 11px; }
  .footer { text-align: center; margin-top: 28px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 11px; }
  @media print { .noprint { display: none !important; } }
  .actions { position: fixed; top: 10px; right: 10px; }
  .actions button { padding: 8px 14px; border: 0; background: #1e3a8a; color: white; border-radius: 6px; cursor: pointer; font-weight: 600; }
</style></head>
<body>
  <div class="actions noprint"><button onclick="window.print()">Print / Save PDF</button></div>
  <h1>JOB COMPLETION CERTIFICATE</h1>
  <div class="top">
    <div class="company">
      <p>KARIBUNET LIMITED</p>
      <p>P. O. Box 221</p>
      <p>ZANZIBAR, TANZANIA</p>
      <p><a href="https://www.karibunet.co.tz">www.karibunet.co.tz</a></p>
      <p><a href="mailto:info@karibunet.co.tz">info@karibunet.co.tz</a></p>
    </div>
    <div class="logo"><img src="/img/karibunet-logo.jpg" alt="KaribuNet" /></div>
  </div>

  <div class="meta">
    <div><b>Work Order No.</b>${esc(record.wo_no) || "—"}</div>
    <div><b>Customer</b>${esc(record.customer) || "—"}</div>
    <div><b>Installation Site</b>${esc(record.installation_site) || "—"}</div>
    <div><b>Technician</b>${esc(record.technician) || "—"}</div>
    <div><b>Scheduled Date</b>${dateFmt(record.scheduled_date)}</div>
    <div><b>Date Completed</b>${dateFmt(record.completed_date)}</div>
  </div>

  <div class="section">
    <h3>Job / Requirements</h3>
    <p>${esc(record.title) || ""}${record.requirements ? `\n\n${esc(record.requirements)}` : ""}</p>
    ${!record.title && !record.requirements ? `<p class="empty">Not recorded</p>` : ""}
  </div>

  <div class="section">
    <h3>Work Performed</h3>
    ${record.work_performed ? `<p>${esc(record.work_performed)}</p>` : `<p class="empty">Not recorded</p>`}
  </div>

  <div class="section">
    <h3>Devices Installed</h3>
    ${devices.length ? `
    <table class="devices">
      <thead><tr><th class="c">#</th><th>Device / Item</th><th>Serial Number</th><th>MAC Address</th><th>Notes</th></tr></thead>
      <tbody>${deviceRows}</tbody>
    </table>` : `<p class="empty">No devices recorded for this job</p>`}
  </div>

  <div class="sign">
    <div class="box"><div class="line">Technician — ${esc(record.technician) || "________________"}</div></div>
    <div class="box"><div class="line">Customer Acknowledgement &amp; Date</div></div>
  </div>

  <div class="footer">This certificate confirms the work and devices above were completed/installed for the customer named. Keep this document for warranty and support reference. &middot; info@karibunet.co.tz &middot; +255 786 093 757</div>
  <script>setTimeout(() => window.print(), 400);</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
}

// Customer statement of account -- every invoice for one customer plus what
// they've paid, viewable on screen (Reports page) and downloadable as PDF
// only (no CSV/Excel for this one, per request).
export function printCustomerLedgerPdf(customerName, invoices) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) return;
  const money = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  // Same statement as the on-screen Customer Ledger (lib/customerStatement.js):
  // every invoice and every dated installment, oldest first, running balance.
  const { entries, totals, undatedCount } = buildStatement(invoices);
  const rows = entries.map((e) => `<tr class="${e.kind}">
      <td>${e.undated ? '<span class="undated">Not recorded</span>' : escHtml(e.date || "—")}</td>
      <td>${escHtml(e.invoice_no)}</td>
      <td class="${e.kind === "payment" ? "pay" : ""}">${escHtml(e.description)}</td>
      <td class="r">${e.debit ? money(e.debit) : ""}</td>
      <td class="r">${e.credit ? money(e.credit) : ""}</td>
      <td class="r">${money(e.balance)}</td>
    </tr>`).join("");
  const totalInvoiced = totals.invoiced, totalPaid = totals.paid;

  const html = `<!doctype html>
<html><head><title>Statement of Account - ${escHtml(customerName)}</title>
<style>
  @page { size: A4; margin: 16mm; }
  body { font-family: 'Times New Roman', Georgia, serif; color: #111; margin: 0; padding: 24px; font-size: 13px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; }
  .company p { margin: 2px 0; font-weight: bold; }
  .logo img { max-width: 150px; }
  .customer { margin-top: 20px; font-size: 15px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  th { background: #1e3a8a; color: #fff; padding: 8px; text-align: left; font-size: 12px; }
  td { padding: 7px 8px; border-bottom: 1px solid #ddd; font-size: 12px; }
  td.r, th.r { text-align: right; }
  tfoot td { padding: 8px; font-weight: bold; background: #f1f5f9; }
  tr.payment td { color: #065f46; }
  td.pay { padding-left: 22px; }
  .undated { color: #b45309; font-style: italic; }
  .note { font-size: 11px; color: #555; margin-top: 8px; }
  .footer { text-align: center; margin-top: 24px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 11px; }
  @media print { .noprint { display: none !important; } }
  .actions { position: fixed; top: 10px; right: 10px; }
  .actions button { padding: 8px 14px; border: 0; background: #1e3a8a; color: white; border-radius: 6px; cursor: pointer; font-weight: 600; }
</style></head>
<body>
  <div class="actions noprint"><button onclick="window.print()">Print / Save PDF</button></div>
  <h1>STATEMENT OF ACCOUNT</h1>
  <div class="top">
    <div class="company">
      <p>KARIBUNET LIMITED</p>
      <p>P. O. Box 221, ZANZIBAR, TANZANIA</p>
      <p>info@karibunet.co.tz &nbsp;|&nbsp; www.karibunet.co.tz</p>
    </div>
    <div class="logo"><img src="/img/karibunet-logo.jpg" alt="KaribuNet" /></div>
  </div>
  <div class="customer">Customer: ${escHtml(customerName)}</div>
  <p style="font-size:11px;color:#555;">As of ${new Date().toLocaleDateString("en-GB")}</p>
  <table>
    <thead><tr><th>Date</th><th>Invoice No.</th><th>Description</th><th class="r">Invoiced</th><th class="r">Paid</th><th class="r">Balance</th></tr></thead>
    <tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#777;">No invoices for this customer</td></tr>`}</tbody>
    <tfoot>
      <tr><td colspan="3">TOTALS</td><td class="r">${money(totalInvoiced)}</td><td class="r">${money(totalPaid)}</td><td class="r">${money(totalInvoiced - totalPaid)}</td></tr>
    </tfoot>
  </table>
  ${undatedCount ? `<p class="note">${undatedCount} payment(s) marked "Not recorded" were received but their exact date was not captured.</p>` : ""}
  <div class="footer">This is a system-generated statement from KARIS &middot; KaribuNet Limited &middot; Kwa Mchina, Zanzibar.</div>
  <script>setTimeout(() => window.print(), 400);</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
}

export function printDocument({ title, subtitle, record, company = "KaribuNet Limited", system = "KARIS" }) {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return;
  const rows = Object.entries(record || {})
    .filter(([k]) => !["id", "_id", "created_by", "updated_by", "approval_status"].includes(k))
    .map(([k, v]) => `
      <tr>
        <td class="k">${label(k)}</td>
        <td class="v">${v === null || v === undefined ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v))}</td>
      </tr>`)
    .join("");

  const docId = record.invoice_no || record.quote_no || record.order_no || record.po_no || record.voucher_no || record.contract_no || record.wo_no || record.id || "";

  const html = `<!doctype html>
<html><head><title>${title} ${docId}</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: 'Manrope', Arial, sans-serif; color: #111; margin: 0; padding: 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 18px; margin-bottom: 28px; }
  .brand { font-family: 'Outfit', Arial, sans-serif; display: flex; align-items: center; gap: 12px; }
  .brand img { width: 42px; height: 42px; object-fit: cover; border-radius: 8px; }
  .brand h1 { margin: 0; font-size: 28px; letter-spacing: -0.5px; background: linear-gradient(90deg, #2563EB, #F59E0B); -webkit-background-clip: text; background-clip: text; color: transparent; }
  .brand p { margin: 4px 0 0; font-size: 11px; color: #555; letter-spacing: 0.18em; text-transform: uppercase; }
  .meta { text-align: right; }
  .meta .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #666; }
  .meta .id { font-family: 'Outfit', Arial, sans-serif; font-size: 22px; font-weight: 600; margin-top: 4px; }
  .meta .date { font-size: 11px; color: #555; }
  h2 { font-family: 'Outfit', Arial, sans-serif; font-size: 22px; margin: 0 0 4px; letter-spacing: -0.3px; }
  .sub { font-size: 12px; color: #666; margin-bottom: 24px; }
  table.detail { width: 100%; border-collapse: collapse; margin-top: 12px; }
  table.detail td { padding: 10px 12px; border-bottom: 1px solid #eee; font-size: 13px; vertical-align: top; }
  table.detail td.k { width: 35%; color: #555; font-weight: 500; }
  table.detail td.v { font-weight: 600; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #eee; font-size: 11px; color: #777; text-align: center; }
  .stamp { display: inline-block; padding: 6px 14px; border: 2px solid #2563EB; color: #2563EB; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; font-size: 12px; border-radius: 999px; }
  @media print { .noprint { display: none !important; } }
  .actions { position: fixed; top: 12px; right: 12px; }
  .actions button { padding: 8px 14px; border: 0; background: linear-gradient(135deg, #2563EB, #F59E0B); color: white; border-radius: 6px; cursor: pointer; font-weight: 600; }
</style></head>
<body>
  <div class="actions noprint">
    <button onclick="window.print()">Print / Save PDF</button>
  </div>
  <div class="header">
    <div class="brand">
      <img src="/img/karibunet-logo.jpg" alt="KaribuNet" />
      <div>
        <h1>${system}</h1>
        <p>${company}</p>
      </div>
    </div>
    <div class="meta">
      <div class="label">${title}</div>
      <div class="id">${docId}</div>
      <div class="date">${new Date().toLocaleDateString("en-GB")}</div>
    </div>
  </div>
  <h2>${title}</h2>
  <div class="sub">${subtitle || ""}</div>
  <table class="detail">${rows}</table>
  <div class="footer">
    <span class="stamp">${(record.status || "Draft").toString().replace(/_/g, " ")}</span>
    <p style="margin-top: 16px;">This is a system-generated document from ${system} · ${company} · Kwa Mchina, Zanzibar.</p>
  </div>
  <script>setTimeout(() => window.print(), 400);</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
}
